import T_be from "../translations/be.json" with { type: "json" };
import T_br from "../translations/br.json" with { type: "json" };
import T_da from "../translations/da.json" with { type: "json" };
import T_de from "../translations/de.json" with { type: "json" };
import T_el from "../translations/el.json" with { type: "json" };
import T_en from "../translations/en.json" with { type: "json" };
import T_eo from "../translations/eo.json" with { type: "json" };
import T_es from "../translations/es.json" with { type: "json" };
import T_fr from "../translations/fr.json" with { type: "json" };
import T_hr from "../translations/hr.json" with { type: "json" };
import T_hu from "../translations/hu.json" with { type: "json" };
import T_it from "../translations/it.json" with { type: "json" };
import T_ja from "../translations/ja.json" with { type: "json" };
import T_nl from "../translations/nl.json" with { type: "json" };
import T_pl from "../translations/pl.json" with { type: "json" };
import T_pt from "../translations/pt.json" with { type: "json" };
import T_pt_BR from "../translations/pt_BR.json" with { type: "json" };
import T_sv from "../translations/sv.json" with { type: "json" };
import T_tr from "../translations/tr.json" with { type: "json" };
import T_uk from "../translations/uk.json" with { type: "json" };
import T_zh_Hant from "../translations/zh_Hant.json" with { type: "json" };

const FALLBACK_LOCALE = "en";
const TRANSLATIONS = {
	"be": T_be, "br": T_br, "da": T_da, "de": T_de, "el": T_el, "eo": T_eo, "en": T_en, "es": T_es, "fr": T_fr,
	"hr": T_hr, "hu": T_hu, "it": T_it, "ja": T_ja, "nl": T_nl, "pl": T_pl, "pt": T_pt, "pt-br": T_pt_BR,
	"sv": T_sv, "tr": T_tr, "uk": T_uk, "zh-hant": T_zh_Hant, "zh": T_zh_Hant,
};

/**
 * Find best matching language regarding of list of supported languages and browser accepted languages
 * @param {str[]} supportedTranslations List of supported languages
 * @param {str} fallback The fallback language
 * @returns The best matching language (in lowercase)
 * @private
 */
export function autoDetectLocale(supportedTranslations, fallback) {
	supportedTranslations = supportedTranslations.map(k => k.toLowerCase());

	for (const navigatorLang of getBrowserLanguages()) {
		let language = navigatorLang.toLowerCase();
		// Convert browser code to weblate code
		switch (language) {
		case "zh-tw":
		case "zh-hk":
		case "zh-mo":
			language = "zh-hant";
			break;
		case "zh-cn":
		case "zh-sg":
			language = "zh-hans";
			break;
		default:
			if (language.length > 2 && !supportedTranslations.includes(language)) {
				language = navigatorLang.substring(0, 2);
			}
			break;
		}
		const pair = supportedTranslations.find((pair) => pair === language);
		if (pair) {
			return pair;
		}
	}
	return fallback;
}

/**
 * Find a sub-property in object based on a path
 * @param {object} obj The multi-level key->value object
 * @param {string[]} path The list of keys to go through
 * @returns {object|string} The retrieved value
 * @private
 */
function getObjectDeepValue(obj, path) {
	if(!obj) { return null; }
    for (let i=0; i < path.length; i++) {
        obj = obj?.[path[i]];
    }
    return obj;
}

/**
 * Translate one-level of a translation JSON
 * @param {object} obj The single level to translated
 * @param {string[]} [path] The past levels of keys to go into
 * @param {string} [lang] The preferred user language
 * @param {string[]} browserPrefs The browser preferred languages
 * @returns {object} The same input object, translated
 * @private
 */
function translateObject(obj, path, lang, browserPrefs) {
	if(!path) { path = []; }

	Object.entries(obj).forEach(([k,v]) => {
		let mypath = path.concat([k]);
		if(typeof v === "string" || Array.isArray(v)) {
			// Look for best-matching label
			//   according to user-defined language or browser prefs

			// First, user-defined language
			let res;
			if(lang) {
				res = getObjectDeepValue(TRANSLATIONS[lang], mypath);
			}
			// Then, user-defined language in short version
			if(!res && lang && lang.length > 2) {
				res = getObjectDeepValue(TRANSLATIONS[lang.substring(0, 2)], mypath);
			}
			// Then, browser preferences
			if(!res && browserPrefs) {
				for(let i=0; i < browserPrefs.length; i++) {
					res = getObjectDeepValue(TRANSLATIONS[browserPrefs[i].toLowerCase()], mypath);
					if(res) { break; }
				}
			}
			// Then, default to english
			if(!res) {
				res = getObjectDeepValue(TRANSLATIONS[FALLBACK_LOCALE], mypath);
			}

			obj[k] = res;

			// Remove empty entries in arrays
			if(Array.isArray(obj[k])) { obj[k] = obj[k].filter(va => va); }
		} else {
			obj[k] = translateObject(obj[k], mypath, lang, browserPrefs);
		}
	});

	return obj;
}

/**
 * Get text labels translations in given language
 *
 * @param {string} [lang] The language code (fr, en) (defaults to browser preferences)
 * @returns {object} Translations in given language, with fallback to english
 * @private
 */
export function getTranslations(lang = "") {
	// No specific lang set -> use browser lang
	if(!lang) {
		lang = autoDetectLocale(Object.keys(TRANSLATIONS), FALLBACK_LOCALE);
	}

	// Create a consistent list of user prefered languages
	let browserPrefs1 = getBrowserLanguages();
	let browserPrefs2 = [];
	browserPrefs1.forEach(l => {
		l = l.toLowerCase();
		if(["zh-tw","zh-hk","zh-mo"].includes(l)) {
			browserPrefs2.push("zh-hant");
		}
		else if(["zh-cn","zh-sg"].includes(l)) {
			browserPrefs2.push("zh-hans");
		}
		else if(l.length > 2) {
			browserPrefs2.push(l);
			browserPrefs2.push(l.substring(0, 2));
		}
		else if(!browserPrefs2.includes(l)) {
			browserPrefs2.push(l);
		}
	});

	// Translate each label to preferred language
	let myTr = JSON.parse(JSON.stringify(T_en));
	myTr = translateObject(myTr, null, lang, browserPrefs2);
	myTr.PREF_LANG = lang;

	return myTr;
}


/**
 * Get best matching label based on user language
 * @param {object} labels The key->value list of labels
 * @param {string} [lang] The language code (fr, en) (defaults to browser preferences)
 * @returns {string} The best matching label
 */
export function getBestLabel(labels, lang) {
	// All to lowercase to avoid matching issues
	lang = lang ? lang.toLowerCase() : null;
	labels = Object.fromEntries(Object.entries(labels).map(([k,v]) => [k.toLowerCase(), v]));

	// No specific lang set -> use browser lang
	if(!lang) {
		lang = autoDetectLocale(Object.keys(labels), FALLBACK_LOCALE);
	}

	if(labels[lang]) { return labels[lang]; }
	else if(lang.length > 2 && labels[lang.substring(0,2)]) { return labels[lang.substring(0,2)]; }
	else { return labels[FALLBACK_LOCALE] || ""; }
}


/**
 * Clean-up browser languages (avoid empty list, empty string...)
 * @returns {string[]} Clean list of browser supported languages
 */
export function getBrowserLanguages() {
	let bl = [];
	if(window.navigator.languages) {
		bl = window.navigator.languages;
	}
	else if(window.navigator.language) {
		bl = [ window.navigator.language ];
	}
	bl = bl.filter(l => l && typeof l === "string" && l.length > 0);
	return bl;
}
